import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Loader2,
  AlertTriangle,
  Search,
  Check,
  ExternalLink,
  Link2,
  Link2Off,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AdminShell from "../components/AdminShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AsinPill = ({ deep, asin }) => (
  <span
    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${
      deep
        ? "bg-emerald-400/10 text-emerald-300 border-emerald-400/30"
        : "bg-white/5 text-white/55 border-white/10"
    }`}
    data-testid="product-target-pill"
  >
    {deep ? (
      <>
        <Link2 className="h-3 w-3" /> deep
      </>
    ) : (
      <>
        <Link2Off className="h-3 w-3" /> search
      </>
    )}
    {deep && asin && (
      <span className="font-mono text-[9px] tracking-normal text-emerald-300/70 ml-1">
        {asin}
      </span>
    )}
  </span>
);

const ProductRow = ({ row, onSaved }) => {
  // We seed the input with the *override* ASIN only — not the static
  // one — so the founder can clearly see what they've personally set.
  const [draft, setDraft] = useState(row.override_asin || "");
  const [saving, setSaving] = useState(false);
  const dirty = draft.trim().toUpperCase() !== (row.override_asin || "").toUpperCase();

  const save = async () => {
    if (saving || !dirty) return;
    // Accept either a 10-char ASIN or a full Amazon URL — extract from
    // either of these formats:
    //   https://www.amazon.com/dp/B0XXXXXXXX/...
    //   https://www.amazon.com/Brand-Name/dp/B0XXXXXXXX/ref=...
    //   https://amzn.to/abc      (short links — user has to expand first)
    let next = draft.trim();
    const dpMatch = next.match(/\/dp\/([A-Z0-9]{10})\b/i);
    if (dpMatch) {
      next = dpMatch[1].toUpperCase();
    } else if (/^https?:\/\//i.test(next)) {
      // Looks like a URL but no /dp/ — refuse rather than guess.
      toast.error(
        "That doesn't look like an Amazon product URL. Click the product on Amazon and copy the URL from the browser bar.",
      );
      return;
    } else {
      next = next.toUpperCase();
    }
    if (next && !/^[A-Z0-9]{10}$/.test(next)) {
      toast.error("ASIN must be exactly 10 alphanumeric characters.");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/products/${row.id}`,
        { amazon_asin: next },
        { withCredentials: true },
      );
      toast.success(
        next ? `${row.brand} now deep-links to ${next}` : "Override cleared",
      );
      onSaved({ ...row, override_asin: next, effective_asin: next || row.static_asin || "", is_deep_link: !!(next || row.static_asin) });
      setDraft(next);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save ASIN.");
    } finally {
      setSaving(false);
    }
  };

  // Direct preview link to the page the user would land on. Lets the
  // founder verify they've pasted the correct ASIN before relying on it.
  const previewHref = row.is_deep_link
    ? `https://www.amazon.com/dp/${row.effective_asin}/`
    : `https://www.amazon.com/s?k=${encodeURIComponent(`${row.brand} ${row.name}`)}`;

  return (
    <div
      className="grid grid-cols-12 gap-3 px-5 py-4 items-center text-sm border-b border-white/5"
      data-testid={`product-row-${row.id}`}
    >
      <div className="col-span-4 min-w-0">
        <div className="text-white/85 truncate">{row.name}</div>
        <div className="text-[11px] text-white/45 truncate">
          {row.brand} · {row.category}
        </div>
      </div>
      <div className="col-span-1 text-center text-white/75 tabular-nums">
        {row.clicks.toLocaleString()}
      </div>
      <div className="col-span-2">
        <AsinPill deep={row.is_deep_link} asin={row.effective_asin} />
      </div>
      <div className="col-span-3">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder={row.static_asin || "Paste ASIN or Amazon URL"}
          className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono tracking-wider text-white placeholder-white/25 focus:outline-none focus:border-[#D4B483]/50 truncate"
          data-testid={`product-asin-input-${row.id}`}
          spellCheck={false}
        />
      </div>
      <div className="col-span-2 flex items-center justify-end gap-2">
        <a
          href={previewHref}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-full border transition-all ${
            row.is_deep_link
              ? "bg-white/[0.04] border-white/10 text-white/65 hover:text-white hover:border-white/25"
              : "bg-[#D4B483]/15 border-[#D4B483]/35 text-[#D4B483] hover:bg-[#D4B483]/25"
          }`}
          title={
            row.is_deep_link
              ? "Open the deep-linked Amazon product page"
              : "Search Amazon for this product, then copy the ASIN from the URL"
          }
          data-testid={`product-preview-${row.id}`}
        >
          {row.is_deep_link ? "Verify" : "Find ASIN"}
          <ExternalLink className="h-3 w-3" />
        </a>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className={`inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] rounded-full px-3 py-1.5 transition-all ${
            dirty
              ? "bg-[#D4B483] text-[#2C2123] hover:bg-[#C0A175]"
              : "bg-white/5 text-white/30 cursor-not-allowed"
          }`}
          data-testid={`product-save-${row.id}`}
        >
          {saving ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Check className="h-3 w-3" />
          )}
          Save
        </button>
      </div>
    </div>
  );
};

const AdminProducts = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | deep | search

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: resp } = await axios.get(`${API}/admin/products`, {
          withCredentials: true,
        });
        if (!cancelled) setData(resp);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.status === 403
              ? "You're signed in, but this view is admin-only."
              : "We couldn't load the product editor.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSaved = (updated) => {
    setData((d) => ({
      ...d,
      products: d.products.map((p) => (p.id === updated.id ? updated : p)),
    }));
  };

  const visible = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.products.filter((p) => {
      if (filter === "deep" && !p.is_deep_link) return false;
      if (filter === "search" && p.is_deep_link) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
      );
    });
  }, [data, query, filter]);

  if (loading) {
    return (
      <AdminShell title="Products & ASIN editor">
        <div
          className="flex items-center gap-3 text-white/70"
          data-testid="admin-products-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading the catalog…
        </div>
      </AdminShell>
    );
  }
  if (error) {
    return (
      <AdminShell title="Products & ASIN editor">
        <div
          className="bg-red-500/10 border border-red-400/30 rounded-2xl p-6 text-red-200"
          data-testid="admin-products-error"
        >
          <AlertTriangle className="h-5 w-5 mb-2" />
          {error}
        </div>
      </AdminShell>
    );
  }

  const deepCount = data.products.filter((p) => p.is_deep_link).length;
  const searchCount = data.total - deepCount;

  return (
    <AdminShell title="Products & ASIN editor">
      <div data-testid="admin-products-root">
        {/* Sub-nav: ASIN overrides | Agent seeder */}
        <div className="flex items-center gap-2 text-sm mb-5">
          <span
            className="px-3 py-1.5 rounded-full bg-[#D4B483]/20 text-[#D4B483] border border-[#D4B483]/30"
            data-testid="products-tab-active"
          >
            ASIN overrides
          </span>
          <Link
            to="/admin/products/seed"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition"
            data-testid="products-tab-seeder"
          >
            <Wand2 className="h-3.5 w-3.5" /> Agent seeder
          </Link>
        </div>
        {/* Intro */}
        <div className="bg-emerald-400/10 border border-emerald-400/25 rounded-2xl p-5 mb-4">
          <h2 className="text-sm text-emerald-300 uppercase tracking-[0.22em] mb-2">
            ✓ Commissions are already live
          </h2>
          <p className="text-sm text-white/75 leading-relaxed">
            Every product link in the app is already tagged with{" "}
            <span className="font-mono text-xs text-emerald-300">
              tag=trubeaute-20
            </span>
            . When a user clicks <em>any</em> product in a routine and buys
            anything on Amazon within 24 hours, you earn a commission —{" "}
            <strong>without you doing a thing</strong>.
          </p>
        </div>
        <div className="bg-[#D4B483]/10 border border-[#D4B483]/25 rounded-2xl p-5 mb-6">
          <h2 className="text-sm text-[#D4B483] uppercase tracking-[0.22em] mb-2">
            What ASINs are for (optional, but earns more)
          </h2>
          <p className="text-sm text-white/75 leading-relaxed">
            By default, products without an ASIN send the user to an
            Amazon{" "}
            <span className="font-mono text-xs">/s?k=...</span> search
            page (still tagged, still earning). With an ASIN, they land
            on the <em>exact</em> product page (
            <span className="font-mono text-xs text-[#D4B483]">
              amazon.com/dp/B0XXXXXXXX/
            </span>
            ) — that converts roughly 3–5× better because they aren't
            tempted by competitors on a results page.
          </p>
          <p className="text-xs text-white/55 mt-3">
            <strong className="text-white/75">How to deep-link:</strong>{" "}
            click <span className="text-[#D4B483]">"Find ASIN"</span> on
            any row → it opens an Amazon search → click the right product
            → <strong>copy the entire Amazon URL from the browser bar</strong>
            {" "}→ paste it into the field → Save. The 10-char ASIN gets
            extracted automatically. Takes about 15 seconds per product.
          </p>
        </div>

        {/* Stats + filter */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="text-xs text-white/55">
              <span className="text-white/85 tabular-nums">
                {data.total}
              </span>{" "}
              products ·{" "}
              <span className="text-emerald-300 tabular-nums">
                {deepCount}
              </span>{" "}
              deep-linked ·{" "}
              <span className="text-white/65 tabular-nums">{searchCount}</span>{" "}
              on search
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[
              { key: "all", label: "All" },
              { key: "search", label: "Need ASIN" },
              { key: "deep", label: "Deep-linked" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border transition-all ${
                  filter === f.key
                    ? "bg-[#D4B483]/20 border-[#D4B483]/40 text-[#D4B483]"
                    : "bg-white/[0.03] border-white/10 text-white/55 hover:text-white/85"
                }`}
                data-testid={`product-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
            <div className="relative ml-2">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Brand, name, category…"
                className="bg-white/[0.04] border border-white/10 rounded-full pl-8 pr-3 py-1.5 text-xs text-white placeholder-white/35 w-56 focus:outline-none focus:border-white/25"
                data-testid="product-search-input"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-white/45 border-b border-white/10">
            <div className="col-span-4">Product</div>
            <div className="col-span-1 text-center">Clicks</div>
            <div className="col-span-2">Target</div>
            <div className="col-span-3">ASIN override</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {visible.length === 0 && (
            <p className="px-5 py-10 text-sm text-white/40 italic text-center">
              No products match.
            </p>
          )}
          {visible.map((row) => (
            <ProductRow key={row.id} row={row} onSaved={onSaved} />
          ))}
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminProducts;
